/**
 * Console Interceptor Test Component
 *
 * Component pour tester le système de monkey patch de console
 * et vérifier que les logs sont bien envoyés au backend.
 */

import React, { useEffect, useState } from "react";
import { ConsoleInterceptor } from "../services/ConsoleInterceptor";
import { LoggerService } from "../services/LoggerService";

export const ConsoleInterceptorTest: React.FC = () => {
    const [ debugMode, setDebugMode ] = useState( false );
    const [ interceptorActive, setInterceptorActive ] = useState( false );
    const [ queueSize, setQueueSize ] = useState( 0 );
    const [ lastTest, setLastTest ] = useState<string>( "" );

    const updateStatus = () => {
        setDebugMode( LoggerService.isDebugModeEnabled() );
        setInterceptorActive( ConsoleInterceptor.isActive() );
        setQueueSize( LoggerService.getQueueSize() );
    };

    useEffect( () => {
        setTimeout( () => updateStatus(), 0 );
        const interval = setInterval( updateStatus, 1000 );

        return () => clearInterval( interval );
    }, [] );

    const handleToggleDebug = () => {
        const newMode = !debugMode;
        LoggerService.setDebugMode( newMode );
        setLastTest( `Mode debug ${ newMode ? "activé" : "désactivé" }` );
        updateStatus();
    };

    const handleTestLog = () => {
        console.log( "🧪 Test console.log", {
            "timestamp": new Date().toISOString(),
            "type":      "log",
        } );
        setLastTest( "console.log envoyé" );
    };

    const handleTestInfo = () => {
        console.info( "ℹ️ Test console.info", {
            "timestamp": new Date().toISOString(),
            "type":      "info",
        } );
        setLastTest( "console.info envoyé" );
    };

    const handleTestWarn = () => {
        console.warn( "⚠️ Test console.warn", {
            "timestamp": new Date().toISOString(),
            "type":      "warning",
            "message":   "Ceci est un avertissement de test",
        } );
        setLastTest( "console.warn envoyé" );
    };

    const handleTestError = () => {
        console.error( "❌ Test console.error", {
            "timestamp": new Date().toISOString(),
            "type":      "error",
            "error":     "Ceci est une erreur de test",
            "code":      "TEST_ERROR",
        } );
        setLastTest( "console.error envoyé" );
    };

    const handleTestDebug = () => {
        console.debug( "🐛 Test console.debug", {
            "timestamp": new Date().toISOString(),
            "type":      "debug",
        } );
        setLastTest( "console.debug envoyé" );
    };

    const handleTestWithData = () => {
        console.log( "📊 Test avec données complexes", {
            "user": {
                "id":    123,
                "name":  "Test User",
                "email": "test@example.com",
            },
            "location": {
                "latitude":  46.5197,
                "longitude": 6.6323,
                "city":      "Lausanne",
            },
            "metadata": {
                "timestamp": Date.now(),
                "source":    "ConsoleInterceptorTest",
                "nested":    {
                    "deep": {
                        "value": "test profond",
                        "array": [ 1, 2, 3 ],
                    },
                },
            },
        } );
        setLastTest( "Log avec données complexes envoyé" );
    };

    const handleTestMultiple = () => {
        console.log( "📝 Test 1/5" );
        console.info( "📝 Test 2/5 (info)" );
        console.log( "📝 Test 3/5 avec données", {
            "index": 3,
            "value": "test",
        } );
        console.warn( "📝 Test 4/5 (warning)" );
        console.log( "📝 Test 5/5 - Série terminée ✅" );
        setLastTest( "5 logs envoyés en série" );
    };

    const handleTestLoggerService = () => {
        /*
         * LoggerService.debug( "Test direct LoggerService.debug", "TestComponent", {
         *     "method": "direct",
         *     "source": "LoggerService",
         * } );
         */
        setLastTest( "LoggerService.debug appelé directement" );
    };

    const handleFlushQueue = async() => {
        await LoggerService.flush();
        setLastTest( "Queue vidée (flush manuel)" );
        updateStatus();
    };

    const handleClearQueue = () => {
        LoggerService.clearQueue();
        setLastTest( "Queue effacée" );
        updateStatus();
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2>
🧪 Test Console Interceptor
                </h2>
                <p>
Vérification du système de monkey patch de console
                </p>
            </div>
            {/* Status Section */}
            <div style={styles.statusCard}>
                <h3>
📊 État du Système
                </h3>
                <div style={styles.statusGrid}>
                    <div style={styles.statusItem}>
                        <span style={styles.statusLabel}>
Mode Debug:
                        </span>
                        <span
                            style={{
                                ...styles.statusValue,
                                "color": debugMode ? "#4CAF50" : "#f44336",
                            }}
                        >
                            {debugMode ? "✅ ACTIVÉ" : "❌ DÉSACTIVÉ"}
                        </span>
                    </div>
                    <div style={styles.statusItem}>
                        <span style={styles.statusLabel}>
Interceptor:
                        </span>
                        <span
                            style={{
                                ...styles.statusValue,
                                "color": interceptorActive ? "#4CAF50" : "#f44336",
                            }}
                        >
                            {interceptorActive ? "✅ ACTIF" : "❌ INACTIF"}
                        </span>
                    </div>
                    <div style={styles.statusItem}>
                        <span style={styles.statusLabel}>
Queue:
                        </span>
                        <span style={styles.statusValue}>
{queueSize}
{" "}
logs en attente
                        </span>
                    </div>
                </div>
                {lastTest && <div style={styles.lastTest}>
Dernier test:
{lastTest}
                             </div>}
            </div>
            {/* Control Section */}
            <div style={styles.section}>
                <h3>
🎛️ Contrôle
                </h3>
                <div style={styles.buttonGroup}>
                    <button
                        onClick={handleToggleDebug}
                        style={{
                            ...styles.button,
                            "backgroundColor": debugMode ? "#f44336" : "#4CAF50",
                        }}
                    >
                        {debugMode ? "❌ Désactiver Debug" : "✅ Activer Debug"}
                    </button>
                    <button
                        onClick={handleFlushQueue}
                        style={styles.button}
                    >
                        🚀 Vider la Queue
                    </button>
                    <button
                        onClick={handleClearQueue}
                        style={styles.button}
                    >
                        🗑️ Effacer la Queue
                    </button>
                </div>
            </div>
            {/* Console Tests Section */}
            <div style={styles.section}>
                <h3>
📝 Tests Console
                </h3>
                <div style={styles.buttonGroup}>
                    <button
                        onClick={handleTestLog}
                        style={styles.button}
                    >
                        console.log
                    </button>
                    <button
                        onClick={handleTestDebug}
                        style={styles.button}
                    >
                        console.debug
                    </button>
                    <button
                        onClick={handleTestInfo}
                        style={styles.button}
                    >
                        console.info
                    </button>
                    <button
                        onClick={handleTestWarn}
                        style={styles.button}
                    >
                        console.warn
                    </button>
                    <button
                        onClick={handleTestError}
                        style={styles.button}
                    >
                        console.error
                    </button>
                </div>
            </div>
            {/* Advanced Tests Section */}
            <div style={styles.section}>
                <h3>
🔬 Tests Avancés
                </h3>
                <div style={styles.buttonGroup}>
                    <button
                        onClick={handleTestWithData}
                        style={styles.button}
                    >
                        📊 Données Complexes
                    </button>
                    <button
                        onClick={handleTestMultiple}
                        style={styles.button}
                    >
                        🔄 Tests Multiples (5x)
                    </button>
                    <button
                        onClick={handleTestLoggerService}
                        style={styles.button}
                    >
                        🎯 LoggerService Direct
                    </button>
                </div>
            </div>
            {/* Instructions Section */}
            <div style={styles.infoCard}>
                <h3>
📖 Instructions
                </h3>
                <ol style={styles.instructionsList}>
                    <li>
Ouvrez la console du navigateur (F12)
                    </li>
                    <li>
Ouvrez les logs du backend (terminal NestJS)
                    </li>
                    <li>
Activez le mode debug ci-dessus
                    </li>
                    <li>
Cliquez sur les boutons de test
                    </li>
                    <li>
                        Vérifiez que les logs apparaissent dans la console backend avec le préfixe
{" "}
                        <code>
[FrontendLogs]
                        </code>
                    </li>
                </ol>
                <div style={styles.note}>
                    <strong>
Note importante:
                    </strong>
{" "}
Les
<code>
console.error
</code>
{" "}
sont toujours
                    envoyés au serveur, même en mode non-debug. Les autres types de logs ne sont
                    envoyés qu&apos;en mode debug.
                </div>
            </div>
            {/* Expected Output Section */}
            <div style={styles.infoCard}>
                <h3>
✅ Résultat Attendu (Backend)
                </h3>
                <pre style={styles.codeBlock}>
                    {`[FrontendLogs] [Frontend:client-xxx][Console] 🧪 Test console.log
{
  "timestamp": "2025-10-11T...",
  "type": "log"
}

[FrontendLogs] [Frontend:client-xxx][Console] ⚠️ Test console.warn
{
  "timestamp": "2025-10-11T...",
  "type": "warning",
  "message": "Ceci est un avertissement de test"
}

[FrontendLogs] [Frontend:client-xxx][Console] ❌ Test console.error
{
  "timestamp": "2025-10-11T...",
  "type": "error",
  "error": "Ceci est une erreur de test",
  "code": "TEST_ERROR"
}`}
                </pre>
            </div>
        </div>
    );
};

// Styles inline pour un composant autonome
const styles: Record<string, React.CSSProperties> = {
    "container": {
        "padding":    "20px",
        "maxWidth":   "900px",
        "margin":     "0 auto",
        "fontFamily": "Arial, sans-serif",
    },
    "header": {
        "textAlign":    "center",
        "marginBottom": "30px",
    },
    "statusCard": {
        "backgroundColor": "#f5f5f5",
        "padding":         "20px",
        "borderRadius":    "8px",
        "marginBottom":    "20px",
    },
    "statusGrid": {
        "display":             "grid",
        "gridTemplateColumns": "repeat(auto-fit, minmax(200px, 1fr))",
        "gap":                 "15px",
        "marginTop":           "15px",
    },
    "statusItem": {
        "display":       "flex",
        "flexDirection": "column",
        "gap":           "5px",
    },
    "statusLabel": {
        "fontSize":   "14px",
        "color":      "#666",
        "fontWeight": "bold",
    },
    "statusValue": {
        "fontSize":   "16px",
        "fontWeight": "bold",
    },
    "lastTest": {
        "marginTop":       "15px",
        "padding":         "10px",
        "backgroundColor": "#e3f2fd",
        "borderRadius":    "4px",
        "fontSize":        "14px",
        "color":           "#1976d2",
    },
    "section":     { "marginBottom": "25px" },
    "buttonGroup": {
        "display":   "flex",
        "flexWrap":  "wrap",
        "gap":       "10px",
        "marginTop": "10px",
    },
    "button": {
        "padding":         "10px 20px",
        "fontSize":        "14px",
        "cursor":          "pointer",
        "border":          "none",
        "borderRadius":    "5px",
        "backgroundColor": "#2196F3",
        "color":           "white",
        "fontWeight":      "bold",
        "transition":      "all 0.3s ease",
    },
    "infoCard": {
        "backgroundColor": "#fff3e0",
        "padding":         "20px",
        "borderRadius":    "8px",
        "marginBottom":    "20px",
    },
    "instructionsList": {
        "lineHeight":  "1.8",
        "paddingLeft": "20px",
    },
    "note": {
        "marginTop":       "15px",
        "padding":         "10px",
        "backgroundColor": "#fff9c4",
        "borderLeft":      "4px solid #fbc02d",
        "borderRadius":    "4px",
        "fontSize":        "14px",
    },
    "codeBlock": {
        "backgroundColor": "#263238",
        "color":           "#aed581",
        "padding":         "15px",
        "borderRadius":    "5px",
        "overflowX":       "auto",
        "fontSize":        "13px",
        "lineHeight":      "1.6",
    },
};


